import React from 'react';
import { Button, Checkbox, Select, Table, TableColumn } from 'common/components';
import {
    ReleaseDecisionResult,
    ReleaseProposal,
    SpamReplacementResult,
    VerdictIngestNumber,
    VerdictIngestResult,
} from 'services/api/NumberRegistrationAPI';
import { useGetReleaseProposals } from 'services/hooks/queries/useGetReleaseProposals';
import { useUploadVerdicts } from 'services/hooks/mutations/useUploadVerdicts';
import { useDecideReleaseProposals, ReleaseDecisionAction } from 'services/hooks/mutations/useDecideReleaseProposals';
import { Tenant } from 'types/accessManager.type';
import styles from '../PhoneNumberManager.module.scss';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

interface ReplaceSpamTabProps {
    tenants: Tenant[];
    isAccessManagerLoading: boolean;
    showNotification: (message: string, type: 'success' | 'error') => void;
}

const errorMessageFrom = (error: any, fallback: string): string =>
    error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;

const ReplaceSpamTab: React.FC<ReplaceSpamTabProps> = ({ tenants, isAccessManagerLoading, showNotification }) => {
    const [tenantId, setTenantId] = React.useState<string>('');
    const [csvFile, setCsvFile] = React.useState<File | null>(null);
    const [ingest, setIngest] = React.useState<VerdictIngestResult | null>(null);
    const [selected, setSelected] = React.useState<string[]>([]);
    const [decision, setDecision] = React.useState<ReleaseDecisionResult | null>(null);

    const { releaseProposals: proposals, isReleaseProposalsLoading: isLoadingProposals } =
        useGetReleaseProposals(tenantId);
    const { uploadVerdicts, isUploadingVerdicts: isUploading } = useUploadVerdicts();
    const { decideReleaseProposals, isDecidingReleaseProposals: isDeciding } = useDecideReleaseProposals();

    React.useEffect(() => {
        setIngest(null);
        setDecision(null);
        setSelected([]);
    }, [tenantId]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            showNotification(
                `File size exceeds maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)} MB. Please upload a smaller file.`,
                'error'
            );
            event.target.value = '';
            return;
        }

        if (!file.name.toLowerCase().endsWith('.csv')) {
            showNotification('Please upload a valid CSV file.', 'error');
            event.target.value = '';
            return;
        }

        setCsvFile(file);
        setIngest(null);
    };

    const handleUpload = async () => {
        if (!tenantId) {
            showNotification('Please select a tenant', 'error');
            return;
        }
        if (!csvFile) {
            showNotification('Please select a CSV file', 'error');
            return;
        }

        setIngest(null);
        setDecision(null);

        try {
            const result = await uploadVerdicts({ tenantId, file: csvFile });
            setIngest(result);
            const summary =
                `Parsed ${result.rowsParsed} rows, resolved ${result.checksResolved} checks, ` +
                `proposed ${result.releaseProposals.length} numbers for release.`;
            showNotification(result.message || summary, 'success');
        } catch (error: any) {
            console.error('Error uploading verdicts:', error);
            showNotification(errorMessageFrom(error, 'Failed to upload verdicts. Please try again.'), 'error');
        }
    };

    const decide = async (action: ReleaseDecisionAction) => {
        if (selected.length === 0) {
            return;
        }

        try {
            const result = await decideReleaseProposals({ tenantId, phoneNumbers: selected, action });
            setDecision(result);
            setSelected([]);

            const verb = action === 'approve' ? 'Released and replaced' : 'Quarantined';
            const acted = action === 'approve' ? result.approved.length : result.rejected.length;
            const refused = result.refused.length;
            showNotification(
                `${verb} ${acted} of ${result.numbersAsked} numbers` + (refused ? ` (${refused} refused).` : '.'),
                refused ? 'error' : 'success'
            );
        } catch (error: any) {
            console.error(`Error on release ${action}:`, error);
            showNotification(errorMessageFrom(error, `Failed to ${action} release proposals.`), 'error');
        }
    };

    const toggle = (phoneNumber: string) => {
        setSelected((current) =>
            current.includes(phoneNumber) ? current.filter((n) => n !== phoneNumber) : [...current, phoneNumber]
        );
    };

    const allSelected = proposals.length > 0 && selected.length === proposals.length;

    const proposalColumns: TableColumn<ReleaseProposal>[] = [
        {
            key: 'select',
            title: (
                <Checkbox
                    checked={allSelected}
                    indeterminate={selected.length > 0 && !allSelected}
                    onChange={() => setSelected(allSelected ? [] : proposals.map((p) => p.phoneNumber))}
                />
            ),
            width: 40,
            render: (_, record) => (
                <Checkbox checked={selected.includes(record.phoneNumber)} onChange={() => toggle(record.phoneNumber)} />
            ),
        },
        {
            key: 'phoneNumber',
            title: 'Phone Number',
            dataIndex: 'phoneNumber',
        },
        {
            key: 'proposedAt',
            title: 'Proposed',
            render: (_, record) => new Date(record.proposedAt).toLocaleString(),
        },
        {
            key: 'lastVerdictAt',
            title: 'Last Verdict',
            render: (_, record) => (record.lastVerdictAt ? new Date(record.lastVerdictAt).toLocaleString() : '-'),
        },
    ];

    const ingestColumns: TableColumn<VerdictIngestNumber>[] = [
        {
            key: 'phoneNumber',
            title: 'Phone Number',
            dataIndex: 'phoneNumber',
        },
        {
            key: 'rawDisplayResult',
            title: 'Display Result',
            dataIndex: 'rawDisplayResult',
        },
        {
            key: 'verdict',
            title: 'Verdict',
            dataIndex: 'verdict',
            width: 110,
        },
        {
            key: 'status',
            title: 'Lifecycle',
            render: (_, record) => record.status || '-',
        },
        {
            key: 'releaseProposed',
            title: 'Release Proposed',
            render: (_, record) => (
                <span className={record.releaseProposed ? styles.statusFailed : styles.statusSuccess}>
                    {record.releaseProposed ? 'Yes' : 'No'}
                </span>
            ),
            width: 140,
        },
        {
            key: 'error',
            title: 'Error',
            render: (_, record) => record.error || '-',
        },
    ];

    const replacements: SpamReplacementResult[] = decision?.replacement?.results ?? [];

    const replacementColumns: TableColumn<SpamReplacementResult>[] = [
        {
            key: 'status',
            title: 'Status',
            render: (_, record) => (
                <span className={record.success ? styles.statusSuccess : styles.statusFailed}>
                    {record.success ? '✓ Success' : '✗ Failed'}
                </span>
            ),
            width: 120,
        },
        {
            key: 'oldNumber',
            title: 'Old Number',
            dataIndex: 'oldNumber',
        },
        {
            key: 'newNumber',
            title: 'New Number',
            render: (_, record) => record.newNumber || '-',
        },
        {
            key: 'tenantName',
            title: 'Tenant',
            dataIndex: 'tenantName',
        },
        {
            key: 'areaCode',
            title: 'Area Code',
            render: (_, record) => (record.areaCode ? String(record.areaCode) : '-'),
            width: 100,
        },
        {
            key: 'error',
            title: 'Error',
            render: (_, record) => record.releaseError || record.purchaseError || '-',
        },
    ];

    const downloadCsv = (content: string, filename: string) => {
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadOreoDelete = () => {
        downloadCsv(replacements.map((result) => result.oldNumber).join('\n'), 'oreo_delete.csv');
        showNotification('Downloaded oreo_delete.csv successfully', 'success');
    };

    const handleDownloadNewOreo = () => {
        const rows = replacements
            .filter((result) => result.success && result.newNumber)
            .map((result) => `${result.newNumber},Multi-Use Line`);

        downloadCsv(rows.join('\n'), 'new_oreo.csv');
        showNotification('Downloaded new_oreo.csv successfully', 'success');
    };

    const tenantOptions = [
        { value: '', label: 'Select a tenant' },
        ...(tenants || []).map((tenant) => ({ value: tenant.id, label: tenant.name })),
    ];

    return (
        <div className={styles.spamContainer}>
            <div className={styles.formSection}>
                <h2 className={styles.sectionTitle}>Upload First Orion Verdicts</h2>
                <p className={styles.description}>
                    Upload the result file from the First Orion portal. The CSV must contain a header row with a "Phone
                    Number" column and a "Display Result" column. Verdicts are recorded against the tenant's numbers
                    with your name attached; a spam verdict <strong>proposes</strong> a release, it never performs one.
                </p>
                <p className={styles.description}>
                    <strong>File Limits:</strong> Maximum file size: {MAX_FILE_SIZE / (1024 * 1024)} MB
                </p>

                <div className={styles.formGroup}>
                    <Select
                        label='Tenant'
                        options={tenantOptions}
                        value={tenantId}
                        onChange={(e) => setTenantId(e.target.value)}
                        disabled={isAccessManagerLoading}
                    />
                </div>

                <div className={styles.uploadSection}>
                    <input
                        type='file'
                        accept='.csv'
                        onChange={handleFileChange}
                        className={styles.fileInput}
                        id='csv-upload'
                    />
                    <label htmlFor='csv-upload' className={styles.fileLabel}>
                        {csvFile ? csvFile.name : 'Choose CSV file'}
                    </label>

                    <Button variant='primary' disabled={!csvFile || !tenantId || isUploading} onClick={handleUpload}>
                        {isUploading ? 'Uploading...' : 'Upload Verdicts'}
                    </Button>
                </div>
            </div>

            {ingest && (
                <div className={styles.resultsSection}>
                    <h2 className={styles.sectionTitle}>
                        Verdicts Recorded — {ingest.rowsParsed} rows, {ingest.checksResolved} checks resolved,{' '}
                        {ingest.releaseProposals.length} proposed for release
                    </h2>
                    {ingest.unmatched.length > 0 && (
                        <p className={styles.warningNote}>
                            Not matched to any number awaiting a verdict: {ingest.unmatched.join(', ')}
                        </p>
                    )}
                    {ingest.numbers.length > 0 && (
                        <Table
                            data={ingest.numbers}
                            columns={ingestColumns}
                            rowKey={(record, index) => `${record.phoneNumber}-${index}`}
                            bordered
                            striped
                            hoverable
                        />
                    )}
                </div>
            )}

            {tenantId && (
                <div className={styles.resultsSection}>
                    <h2 className={styles.sectionTitle}>Release Proposals ({proposals.length})</h2>
                    <p className={styles.description}>
                        Approving releases the number and buys a replacement in the same area code. Rejecting
                        quarantines it instead: the number is kept but stays out of production.
                    </p>
                    {isLoadingProposals ? (
                        <p className={styles.description}>Loading proposals...</p>
                    ) : proposals.length === 0 ? (
                        <p className={styles.description}>No numbers are waiting on a release decision.</p>
                    ) : (
                        <>
                            <Table
                                data={proposals}
                                columns={proposalColumns}
                                rowKey={(record) => record.phoneNumberId}
                                bordered
                                striped
                                hoverable
                            />
                            <div className={styles.buttonContainer}>
                                <Button
                                    variant='secondary'
                                    disabled={selected.length === 0 || isDeciding}
                                    onClick={() => decide('reject')}
                                >
                                    Reject &amp; Quarantine ({selected.length})
                                </Button>
                                <Button
                                    variant='primary'
                                    disabled={selected.length === 0 || isDeciding}
                                    onClick={() => decide('approve')}
                                >
                                    {isDeciding ? 'Working...' : `Approve & Replace (${selected.length})`}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {decision && (
                <div className={styles.resultsSection}>
                    <h2 className={styles.sectionTitle}>
                        Decision by {decision.actedBy} at {new Date(decision.actedAt).toLocaleString()}
                    </h2>
                    {decision.refused.length > 0 && (
                        <p className={styles.warningNote}>
                            Refused (no longer proposed for release): {decision.refused.join(', ')}
                        </p>
                    )}
                    {decision.errors &&
                        Object.entries(decision.errors).map(([phoneNumber, message]) => (
                            <p key={phoneNumber} className={styles.errorText}>
                                {phoneNumber}: {message}
                            </p>
                        ))}
                    {replacements.length > 0 && (
                        <>
                            <Table
                                data={replacements}
                                columns={replacementColumns}
                                rowKey={(record, index) => `${record.oldNumber}-${index}`}
                                rowClassName={(record) => (record.success ? styles.successRow : styles.failureRow)}
                                bordered
                                striped
                                hoverable
                            />
                            <div className={styles.buttonContainer}>
                                <Button variant='secondary' onClick={handleDownloadOreoDelete}>
                                    Download oreo_delete.csv
                                </Button>
                                <Button variant='secondary' onClick={handleDownloadNewOreo}>
                                    Download new_oreo.csv
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReplaceSpamTab;
